import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api';
import { formatDateTime } from '../utils/stringUtils';
import { DeleteButton } from './shared/DeleteButton';
import { usePermissions } from '../context/PermissionsContext';

interface User {
  username: string;
  user_id?: string;
  role: 'administrator' | 'poweruser' | 'user';
  status: 'new' | 'approved' | 'disabled';
  is_primary?: boolean;
  created_at: string;
  updated_at: string;
  last_logged_in?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  department?: string;
  job_title?: string;
  avatar_path?: string;
}

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'administrator' | 'poweruser' | 'user'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'approved' | 'disabled'>('all');

  useEffect(() => {
    fetchUsers();
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await apiClient.get('/profile');
      setCurrentUserId(response.data.user_id);
    } catch (error: any) {
      console.error('Failed to fetch current user:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/users');
      setUsers(response.data);
    } catch (error: any) {
      setError(error.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickApprove = async (user: User) => {
    const userId = user.user_id || user.username;
    try {
      await apiClient.put(`/users/${userId}`, { status: 'approved' });
      setSuccess(`User '${user.username}' approved successfully`);
      setTimeout(() => setSuccess(null), 3000);
      fetchUsers();
    } catch (error: any) {
      setError(error.message || 'Failed to approve user');
    }
  };

  const handleDelete = async (user: User) => {
    const userId = user.user_id || user.username;
    try {
      await apiClient.delete(`/users/${userId}`);
      setSuccess('User deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      fetchUsers();
    } catch (error: any) {
      setError(error.message || 'Failed to delete user');
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'administrator': return 'badge-danger';
      case 'poweruser': return 'badge-warning';
      default: return 'badge-info';
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'new': return 'badge-primary';
      case 'approved': return 'badge-success';
      case 'disabled': return 'badge-secondary';
      default: return 'badge-secondary';
    }
  };

  // Filter users based on search term, role, and status
  const filteredUsers = users.filter(user => {
    // Apply search filter
    const trimmedSearch = searchTerm.trim();
    if (trimmedSearch) {
      const searchLower = trimmedSearch.toLowerCase();
      const matchesSearch = 
        user.username?.toLowerCase().includes(searchLower) ||
        user.first_name?.toLowerCase().includes(searchLower) ||
        user.last_name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.department?.toLowerCase().includes(searchLower) ||
        user.job_title?.toLowerCase().includes(searchLower);
      
      if (!matchesSearch) return false;
    }
    
    // Apply role filter
    if (roleFilter !== 'all' && user.role !== roleFilter) {
      return false;
    }
    
    // Apply status filter
    if (statusFilter !== 'all' && user.status !== statusFilter) {
      return false;
    }
    
    return true;
  });

  return (
    <div className="container-fluid">
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => navigate('/')}
          >
            <i className="fas fa-times"></i> Close
          </button>
          <div style={{ position: 'relative', display: 'inline-block', marginLeft: '0.75rem' }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: '0.875rem', pointerEvents: 'none' }}></i>
            <input
              type="text"
              className="form-control"
              placeholder="Filter Users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '240px', paddingLeft: '2.25rem', backgroundColor: 'rgba(255, 255, 255, 0.3)', border: 'none', fontSize: '0.875rem', outline: 'none', boxShadow: 'none' }}
              onFocus={(e) => e.target.style.boxShadow = 'none'}
            />
          </div>
          <select
            className="form-control dropdown-styling"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as any)}
            style={{ width: '160px', display: 'inline-block', marginLeft: '0.75rem', backgroundColor: 'rgba(255, 255, 255, 0.3)', border: 'none', fontSize: '0.875rem' }}
          >
            <option value="all">All Roles</option>
            <option value="administrator">Administrator</option>
            <option value="poweruser">Power User</option>
            <option value="user">User</option>
          </select>
          <select
            className="form-control"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            style={{ width: '160px', display: 'inline-block', marginLeft: '0.75rem', backgroundColor: 'rgba(255, 255, 255, 0.3)', border: 'none', fontSize: '0.875rem' }}
          >
            <option value="all">All Statuses</option>
            <option value="new">New</option>
            <option value="approved">Approved</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
        <div>
          <button 
            className="btn btn-sm btn-primary"
            onClick={() => navigate('/users/integrations')}
          >
            <i className="fas fa-plug"></i> Integrations
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="close" onClick={() => setError(null)} aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}
      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button type="button" className="close" onClick={() => setSuccess(null)} aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <div className="spinner-border" role="status" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>
            <span className="visually-hidden"></span>
          </div>
        </div>
      ) : users.length === 0 ? (
        <div className="card shadow mb-4">
          <div className="card-body text-center">
            <div className="text-muted">
              <i className="fas fa-users fa-3x mb-3"></i>
              <p>No users found.</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="card shadow mb-4">
          <div className="card-header py-3">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-users"></i> User Management
              <span className="badge badge-primary ml-2" style={{ verticalAlign: 'middle' }}>
                {filteredUsers.length}{searchTerm || roleFilter !== 'all' || statusFilter !== 'all' ? ` of ${users.length}` : ''}
              </span>
            </h6>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover table-sm">
                <thead>
                  <tr>
                    <th>Avatar</th>
                    <th>Username</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr 
                      key={user.username}
                      className={user.status === 'new' ? 'table-light' : ''}
                      onClick={() => setSelectedUser(user)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <img
                          src={user.avatar_path ? `/${user.avatar_path}` : '/avatars/default.png'}
                          alt={user.username}
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            objectFit: 'cover',                            
                          }}
                          onError={(e) => {
                            e.currentTarget.src = '/avatars/default.png';
                          }}
                        />
                      </td>
                      <td>
                        <strong>{user.username}</strong>
                        {user.is_primary && (
                          <span className="badge badge-primary ms-2" style={{marginLeft: '10px'}} title="Primary Administrator">
                            <i className="fas fa-crown"></i> PRIMARY
                          </span>
                        )}
                        {user.status === 'new' && (
                          <span className="badge badge-warning ms-2" style={{marginLeft: '10px'}}>NEEDS APPROVAL</span>
                        )}
                        {currentUserId && user.user_id === currentUserId && (
                          <span className="badge badge-info ms-2" style={{marginLeft: '10px'}}>YOU</span>
                        )}
                      </td>
                      <td>
                        {user.first_name || user.last_name ? (
                          <span>
                            {user.first_name} {user.last_name}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${getRoleBadgeClass(user.role)}`}>
                          {user.role.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(user.status)}`}>
                          {user.status.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <small>
                          {user.last_logged_in ? (
                            formatDateTime(user.last_logged_in, true)
                          ) : (
                            <span className="text-muted">Never</span>
                          )}
                        </small>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {hasPermission('users.edit') && (
                          <>
                            <button
                              className="btn btn-sm btn-primary me-2"
                              onClick={() => navigate(`/users/${user.user_id || user.username}`)}
                              title="Edit user"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            &nbsp;
                          </>
                        )}
                        {hasPermission('users.approve') && user.status === 'new' && (
                          <>
                            <button
                              className="btn btn-sm btn-success me-2"
                              onClick={() => handleQuickApprove(user)}
                              title="Quick approve user"
                            >
                              <i className="fas fa-check"></i> Approve
                            </button>
                            &nbsp;
                          </>
                        )}
                        {hasPermission('users.delete') && user.role !== 'administrator' && !user.is_primary && (
                          <DeleteButton
                            onDelete={() => handleDelete(user)}
                            className="btn-sm"
                            title="Delete user permanently"
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {selectedUser && (
        <div 
          className="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setSelectedUser(null)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-user"></i> User Details - {selectedUser.username}
                </h5>
                <button 
                  type="button" 
                  className="close" 
                  onClick={() => setSelectedUser(null)}
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-4 text-center">
                    <img
                      src={selectedUser.avatar_path ? `/${selectedUser.avatar_path}` : '/avatars/default.png'}
                      alt={selectedUser.username}
                      style={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                      }}
                      onError={(e) => {
                        e.currentTarget.src = '/avatars/default.png';
                      }}
                    />
                  </div>
                  <div className="col-md-8">
                    <div className="mb-2">
                      <strong>Name:</strong> {selectedUser.first_name || selectedUser.last_name ? (
                        <span>{selectedUser.first_name} {selectedUser.last_name}</span>
                      ) : (
                        <span className="text-muted">Not provided</span>
                      )}
                    </div>
                    <div className="mb-2">
                      <strong>Email:</strong> {selectedUser.email || <span className="text-muted">Not provided</span>}
                    </div>
                    <div className="mb-2">
                      <strong>Role:</strong>{' '}
                      <span className={`badge ${getRoleBadgeClass(selectedUser.role)}`}>
                        {selectedUser.role.toUpperCase()}
                      </span>
                    </div>
                    <div className="mb-2">
                      <strong>Status:</strong>{' '}
                      <span className={`badge ${getStatusBadgeClass(selectedUser.status)}`}>
                        {selectedUser.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
                
                <hr />
                
                <div className="mb-2">
                  <strong>Department:</strong> {selectedUser.department || <span className="text-muted">Not provided</span>}
                </div>
                <div className="mb-2">
                  <strong>Job Title:</strong> {selectedUser.job_title || <span className="text-muted">Not provided</span>}
                </div>
                
                <hr />
                
                <div className="mb-2">
                  <strong>Created:</strong>{' '}
                  <span className="text-muted">{formatDateTime(selectedUser.created_at, true)}</span>
                </div>
                <div className="mb-2">
                  <strong>Last Login:</strong>{' '}
                  {selectedUser.last_logged_in ? (
                    <span className="text-muted">{formatDateTime(selectedUser.last_logged_in, true)}</span>
                  ) : (
                    <span className="text-muted">Never</span>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setSelectedUser(null)}
                >
                  Close
                </button>
                {hasPermission('users.delete') && selectedUser.role !== 'administrator' && (
                  <DeleteButton
                    onDelete={() => {
                      handleDelete(selectedUser);
                      setSelectedUser(null);
                    }}
                    variant="danger"
                  >
                    Delete User
                  </DeleteButton>
                )}
                {hasPermission('users.edit') && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      navigate(`/users/${selectedUser.user_id || selectedUser.username}`);
                      setSelectedUser(null);
                    }}
                  >
                    <i className="fas fa-edit"></i> Edit User
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
